/**
 * Build a nested tree of ERP sample fields for the simple mapper UI.
 */

import { buildArrayItemFieldNodes } from './arrayMappingPaths';

const MAX_NODES = 500;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function appendSegment(base, segment) {
  if (base === '$') {
    return `$.${segment}`;
  }

  return `${base}.${segment}`;
}

function formatSampleValue(value) {
  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.length > 48 ? `${value.slice(0, 45)}…` : value;

    return trimmed || '""';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.length}]`;
  }

  if (isPlainObject(value)) {
    return '{…}';
  }

  return String(value);
}

function walk(value, currentPath, nodes, counter) {
  if (counter.count >= MAX_NODES) {
    return;
  }

  if (Array.isArray(value)) {
    counter.count += 1;
    const children = [];
    const limit = Math.min(value.length, 5);

    for (let i = 0; i < limit; i += 1) {
      walk(value[i], appendSegment(currentPath, String(i)), children, counter);
    }

    nodes.push({
      path: currentPath,
      key: currentPath.split('.').pop() || '$',
      kind: 'array',
      sample: formatSampleValue(value),
      children,
    });

    return;
  }

  if (isPlainObject(value)) {
    counter.count += 1;
    const children = [];
    Object.keys(value).forEach((key) => {
      walk(value[key], appendSegment(currentPath, key), children, counter);
    });

    nodes.push({
      path: currentPath,
      key: currentPath === '$' ? 'root' : currentPath.split('.').pop(),
      kind: 'object',
      sample: formatSampleValue(value),
      children,
    });

    return;
  }

  counter.count += 1;
  nodes.push({
    path: currentPath,
    key: currentPath.split('.').pop() || '$',
    kind: 'scalar',
    sample: formatSampleValue(value),
    children: [],
  });
}

/**
 * @param {object|null|undefined} payload Parsed ERP sample
 * @returns {{ path: string, key: string, kind: 'scalar'|'object'|'array', sample: string, children: object[] }[]}
 */
export function buildErpFieldTree(payload) {
  if (payload === null || payload === undefined) {
    return [];
  }

  const counter = { count: 0 };
  const roots = [];

  if (Array.isArray(payload)) {
    walk(payload, '$', roots, counter);
  } else if (isPlainObject(payload)) {
    Object.keys(payload).forEach((key) => {
      walk(payload[key], appendSegment('$', key), roots, counter);
    });
  } else {
    roots.push({
      path: '$',
      key: 'value',
      kind: 'scalar',
      sample: formatSampleValue(payload),
      children: [],
    });
  }

  return roots;
}

/**
 * Flatten tree to draggable leaf/array paths (scalars and arrays only).
 */
export function flattenErpDraggableNodes(tree, query = '') {
  const normalizedQuery = (query || '').trim().toLowerCase();
  const results = [];

  const visit = (nodes) => {
    nodes.forEach((node) => {
      if (node.kind === 'scalar' || node.kind === 'array') {
        const matches =
          !normalizedQuery ||
          node.path.toLowerCase().includes(normalizedQuery) ||
          node.key.toLowerCase().includes(normalizedQuery) ||
          node.sample.toLowerCase().includes(normalizedQuery);

        if (matches) {
          results.push(node);
        }
      }

      if (node.children?.length) {
        visit(node.children);
      }
    });
  };

  visit(tree);

  return results.sort((a, b) => a.path.localeCompare(b.path));
}

/**
 * Filter nested tree by search query (keeps branches that match or have matching descendants).
 */
export function filterErpFieldTree(tree, query) {
  const normalizedQuery = (query || '').trim().toLowerCase();
  if (!normalizedQuery) {
    return tree;
  }

  const filterNodes = (nodes) =>
    nodes
      .map((node) => {
        const filteredChildren = filterNodes(node.children || []);
        const matchesSelf =
          node.path.toLowerCase().includes(normalizedQuery) ||
          node.key.toLowerCase().includes(normalizedQuery) ||
          node.sample.toLowerCase().includes(normalizedQuery);

        if (!matchesSelf && filteredChildren.length === 0) {
          return null;
        }

        return { ...node, children: filteredChildren };
      })
      .filter(Boolean);

  return filterNodes(tree);
}

/**
 * Flatten ERP tree rows for the simple mapper, including array item-relative fields.
 */
export function flattenErpFieldRows(payload, searchQuery = '') {
  const tree = filterErpFieldTree(buildErpFieldTree(payload), searchQuery);

  const rows = [];

  const visit = (nodes, depth = 0) => {
    nodes.forEach((node) => {
      rows.push({ ...node, depth });

      if (node.kind === 'object') {
        visit(node.children || [], depth + 1);

        return;
      }

      if (node.kind === 'array') {
        const itemFields = buildArrayItemFieldNodes(payload, node.path, depth + 1);
        itemFields.forEach((itemField) => {
          rows.push(itemField);
        });
      }
    });
  };

  visit(tree);

  return rows;
}

export function findErpFieldNode(payload, path) {
  if (!payload || !path) {
    return null;
  }

  const rows = flattenErpFieldRows(payload);

  return rows.find((node) => node.path === path) || null;
}
